import { intlLocale } from "@/i18n/intl-locale";
import type { Locale } from "@/i18n/types";
import { wallClockInZone } from "@/lib/zoned-time";

/**
 * Parse a date-only string ("YYYY-MM-DD", or the leading date of a full ISO
 * datetime) into a Date at LOCAL midnight.
 *
 * Why not ``new Date(str)``: JS parses a bare "YYYY-MM-DD" as UTC midnight, so
 * in a behind-UTC timezone it renders as the previous calendar day. Building the
 * Date from local components keeps the day the user actually entered.
 */
export function parseDateOnly(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const date = new Date(year, month - 1, day);
  // Reject overflowed components (e.g. month 13 → rolled into next year).
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** The IANA zone this browser's local dates are in. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * Serialize a local-midnight Date back to a date-only "YYYY-MM-DD" string.
 *
 * The inverse of `parseDateOnly`, and the reason it exists: `toISOString()`
 * converts to UTC first, so local midnight in an *ahead*-of-UTC timezone
 * (Europe, APAC) lands on the previous UTC calendar day and the date silently
 * saves one day early. Reading the local components back out cannot shift.
 */
export function toDateOnlyString(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Format a date-only string as the v3 short date, e.g. "Aug 12, 2026" in
 * English and "12 août 2026" in French.
 *
 * This is the canonical display format for dates across v3 (project
 * properties, work-item quick view, …). `locale` is the reader's app locale
 * (`useTranslation().locale`); it defaults to English so a caller not yet
 * threading it renders exactly as before (NG-2614 converts them). Returns null
 * for a missing or unparseable value so callers can render their own fallback.
 */
export function formatDateShort(
  value: string | null | undefined,
  locale: Locale = "en",
): string | null {
  if (!value) return null;
  const date = parseDateOnly(value);
  if (!date) return null;
  return date.toLocaleDateString(intlLocale(locale), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format a date-only string as a month and two-digit day, e.g. "Mar 05": the
 * compact form a row uses where the year is plain from its neighbours. Reads the
 * local day through `parseDateOnly`, as `formatDateShort` does. Returns null for
 * a missing or unparseable value so callers can render their own fallback.
 */
export function formatMonthDay(
  value: string | null | undefined,
  locale: Locale = "en",
): string | null {
  if (!value) return null;
  const date = parseDateOnly(value);
  if (!date) return null;
  return date.toLocaleDateString(intlLocale(locale), { month: "short", day: "2-digit" });
}

/**
 * Format a date-only span as the v3 short range, e.g. "Dec 25, 2026 - Dec 27, 2026".
 *
 * Collapses to a single date when the span covers one day, so a caller can pass
 * both ends unconditionally instead of branching at every call site. Returns
 * null only when the *start* is missing or unparseable; an absent or
 * unparseable end degrades to the start alone rather than losing the row's date
 * entirely.
 */
export function formatDateRangeShort(
  start: string | null | undefined,
  end: string | null | undefined,
  locale: Locale = "en",
): string | null {
  const from = formatDateShort(start, locale);
  if (!from) return null;
  const to = formatDateShort(end, locale);
  if (!to || to === from) return from;
  return `${from} - ${to}`;
}

/**
 * Format a full ISO *datetime* instant as the v3 short datetime, e.g.
 * "Jul 17, 2026, 3:24 PM", in the viewer's timezone.
 *
 * Unlike `formatDateShort` (a bare "YYYY-MM-DD" must NOT be timezone-shifted),
 * this is for a real instant that carries a time + offset, so `new Date(value)`
 * is correct. `locale` works as in `formatDateShort`, defaulting to English.
 * Returns null for a missing or unparseable value so callers can render their
 * own fallback.
 */
export function formatDateTimeShort(
  value: string | null | undefined,
  locale: Locale = "en",
): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(intlLocale(locale), {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format the calendar day of a full ISO *datetime* instant as the v3 short
 * date, e.g. "Aug 12, 2026" / "12 août 2026", in the viewer's timezone.
 *
 * `formatDateShort` reads only the leading "YYYY-MM-DD", which for an instant is
 * the UTC day: a work item completed at 9 PM in Toronto would show tomorrow.
 * This takes the day where the viewer is, as `formatDateTimeShort` does, and
 * drops the time. Returns null for a missing or unparseable value.
 */
export function formatInstantDateShort(
  value: string | null | undefined,
  locale: Locale = "en",
): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(intlLocale(locale), {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Calendar date (as a local-midnight Date) of an ISO datetime instant in a
 * *specific* IANA timezone — not the viewer's browser timezone, not UTC.
 *
 * For an event stored/serialized in UTC (Django's `.isoformat()` on a
 * USE_TZ=True datetime), the UTC calendar day and the event's own configured
 * timezone's calendar day diverge for anything near midnight — e.g. an event
 * at 11:30pm America/New_York is already the next UTC day. Mirrors the
 * backend's `_event_local_date` (scheduling/services/timeline_aggregation.py:
 * "bucket the bar on the event's own-timezone calendar date, not [UTC]").
 * Returns null if `isoDatetime` doesn't parse to a valid instant.
 */
export function dateInTimeZone(isoDatetime: string | Date, timeZone: string): Date | null {
  // Delegated to `zoned-time`, which is the one zone reader in the app: it caches
  // its formatters per zone and handles an unknown IANA name once, so a second
  // hand-rolled `formatToParts` block here would be a second place for the same
  // fix to be needed (NG-1791).
  const wall = wallClockInZone(isoDatetime, timeZone);
  return wall ? parseDateOnly(wall.date) : null;
}
